import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Image as ImageIcon, Video, Mic, PenLine, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Avatar } from "@/components/Avatar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHUNK_SIZE = 8 * 1024 * 1024;
const CHUNKED_THRESHOLD = 32 * 1024 * 1024;

const TYPE_OPTIONS = [
    { id: "photo", label: "Photo", icon: ImageIcon, accept: "image/*" },
    { id: "video", label: "Video", icon: Video, accept: "video/*" },
    { id: "audio", label: "Voice", icon: Mic, accept: "audio/*" },
    { id: "story", label: "Story", icon: PenLine, accept: "" },
];

const INPUT_CLASS =
    "w-full rounded-2xl border-2 border-warmgray bg-[#FDFBF9] px-5 py-3 text-sm font-medium text-navy outline-none transition-colors duration-300 focus:border-peach";

export function AddMemoryModal({ open, onClose, onSaved, milestones = [], members = [], presetMilestoneId = "" }) {
    const [memType, setMemType] = useState("photo");
    const [file, setFile] = useState(null);
    const [title, setTitle] = useState("");
    const [caption, setCaption] = useState("");
    const [story, setStory] = useState("");
    const [memoryDate, setMemoryDate] = useState("");
    const [location, setLocation] = useState("");
    const [milestoneId, setMilestoneId] = useState("");
    const [tagged, setTagged] = useState([]);
    const [thumbnail, setThumbnail] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [recording, setRecording] = useState(false);
    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const fileRef = useRef(null);

    useEffect(() => {
        if (open) {
            setMilestoneId(presetMilestoneId || "");
        }
    }, [open, presetMilestoneId]);

    if (!open) return null;

    const reset = () => {
        setMemType("photo");
        setFile(null);
        setThumbnail(null);
        setTitle("");
        setCaption("");
        setStory("");
        setMemoryDate("");
        setLocation("");
        setMilestoneId("");
        setTagged([]);
        setRecording(false);
    };

    const close = () => {
        if (uploading) return;
        reset();
        onClose();
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mr = new MediaRecorder(stream);
            chunksRef.current = [];
            mr.ondataavailable = (e) => chunksRef.current.push(e.data);
            mr.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
                const ext = (mr.mimeType || "").includes("mp4") ? "mp4" : "webm";
                setFile(new File([blob], `voice-story-${Date.now()}.${ext}`, { type: blob.type }));
                stream.getTracks().forEach((t) => t.stop());
            };
            mr.start();
            mediaRecorderRef.current = mr;
            setRecording(true);
        } catch {
            toast.error("Microphone access is needed to record a voice story.");
        }
    };

    const stopRecording = () => {
        mediaRecorderRef.current?.stop();
        setRecording(false);
    };

    const generateVideoThumbnail = (f) => {
        try {
            const url = URL.createObjectURL(f);
            const video = document.createElement("video");
            video.muted = true;
            video.playsInline = true;
            video.preload = "auto";
            video.src = url;
            video.onloadeddata = () => {
                video.currentTime = Math.min(0.5, (video.duration || 1) / 2);
            };
            video.onseeked = () => {
                const canvas = document.createElement("canvas");
                canvas.width = video.videoWidth || 640;
                canvas.height = video.videoHeight || 640;
                canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
                canvas.toBlob(
                    (blob) => {
                        if (blob) setThumbnail(new File([blob], "thumbnail.jpg", { type: "image/jpeg" }));
                        URL.revokeObjectURL(url);
                    },
                    "image/jpeg",
                    0.8
                );
            };
            video.onerror = () => URL.revokeObjectURL(url);
        } catch {}
    };

    const onFilePicked = (e) => {
        const f = e.target.files?.[0] || null;
        setFile(f);
        setThumbnail(null);
        if (f && memType === "video") generateVideoThumbnail(f);
    };

    const toggleTag = (id) => {
        setTagged((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]));
    };

    const uploadLargeFile = async (f) => {
        const { data: init } = await axios.post(
            `${API}/uploads/init`,
            { filename: f.name, content_type: f.type, size: f.size },
            { withCredentials: true }
        );
        const chunks = Math.ceil(f.size / CHUNK_SIZE);
        for (let i = 0; i < chunks; i++) {
            const blob = f.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
            await axios.post(`${API}/uploads/${init.upload_id}/chunk?index=${i}`, blob, {
                withCredentials: true,
                headers: { "Content-Type": "application/octet-stream" },
            });
            setProgress(Math.round(((i + 1) / chunks) * 100));
        }
        await axios.post(`${API}/uploads/${init.upload_id}/complete`, {}, { withCredentials: true });
        return init.upload_id;
    };

    const submit = async (e) => {
        e.preventDefault();
        if (memType !== "story" && !file) return;
        if (memType === "story" && !story.trim()) {
            toast.error("Write a few words for your story.");
            return;
        }
        setUploading(true);
        setProgress(0);
        try {
            let uploadId = "";
            if (file && file.size > CHUNKED_THRESHOLD) {
                uploadId = await uploadLargeFile(file);
            }
            const form = new FormData();
            if (uploadId) form.append("upload_id", uploadId);
            else if (file) form.append("file", file);
            if (thumbnail) form.append("thumbnail", thumbnail);
            form.append("caption", caption);
            form.append("memory_type", memType);
            form.append("title", title);
            form.append("story", story);
            form.append("memory_date", memoryDate);
            form.append("location", location);
            form.append("milestone_id", milestoneId);
            form.append("tagged_member_ids", tagged.join(","));
            await axios.post(`${API}/memories`, form, { withCredentials: true });
            reset();
            onClose();
            await onSaved?.();
            toast.success("Memory added to your family archive.");
        } catch (err) {
            toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Upload failed. Please try again.");
        }
        setUploading(false);
        setProgress(0);
    };

    const accept = TYPE_OPTIONS.find((t) => t.id === memType).accept;

    return (
        <div data-testid="add-memory-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 px-4 backdrop-blur-sm" onClick={close}>
            <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-3xl border-2 border-warmgray bg-ivory p-8"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between">
                    <h2 className="font-display text-2xl">Add a Memory</h2>
                    <button data-testid="close-modal-button" onClick={close} aria-label="Close" className="text-navy/60 hover:text-navy">
                        <X size={20} />
                    </button>
                </div>

                <div className="mt-5 grid grid-cols-4 gap-2">
                    {TYPE_OPTIONS.map((t) => (
                        <button
                            key={t.id}
                            data-testid={`type-${t.id}`}
                            onClick={() => {
                                setMemType(t.id);
                                setFile(null);
                                setThumbnail(null);
                                if (fileRef.current) fileRef.current.value = "";
                            }}
                            className={`flex flex-col items-center gap-1.5 rounded-2xl border-2 py-3 text-xs font-semibold transition-colors duration-300 ${
                                memType === t.id ? "border-navy bg-cream text-navy" : "border-warmgray text-navy/70 hover:border-peach"
                            }`}
                        >
                            <t.icon size={18} /> {t.label}
                        </button>
                    ))}
                </div>

                <form onSubmit={submit} className="mt-5 space-y-3">
                    {memType === "audio" && (
                        <div className="rounded-2xl border-2 border-warmgray bg-cream p-4">
                            <button
                                data-testid="record-audio-button"
                                type="button"
                                onClick={recording ? stopRecording : startRecording}
                                className={`flex w-full items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold transition-transform duration-300 hover:-translate-y-0.5 ${
                                    recording ? "bg-red-700 text-white" : "bg-navy text-white"
                                }`}
                            >
                                <Mic size={15} />
                                {recording ? "Stop recording" : "Record a voice story"}
                            </button>
                            {recording && (
                                <p data-testid="recording-indicator" className="mt-2 text-center font-mono text-[11px] uppercase tracking-widest text-red-700">
                                    ● Recording — speak from the heart
                                </p>
                            )}
                            {file && !recording && (
                                <p data-testid="recording-ready" className="mt-2 text-center font-mono text-[11px] uppercase tracking-widest text-navy">
                                    Recording ready · {file.name}
                                </p>
                            )}
                            <p className="mt-2 text-center text-xs text-[#2C3347]">…or upload an existing audio file below</p>
                        </div>
                    )}

                    {memType === "story" ? (
                        <textarea
                            data-testid="memory-story-input"
                            value={story}
                            onChange={(e) => setStory(e.target.value)}
                            placeholder="Tell the story in your own words — the little details are what future generations will love."
                            aria-label="Story"
                            rows={6}
                            className={`${INPUT_CLASS} resize-none rounded-2xl leading-relaxed`}
                        />
                    ) : (
                        <input
                            data-testid="memory-file-input"
                            ref={fileRef}
                            type="file"
                            required={!file}
                            accept={accept}
                            onChange={onFilePicked}
                            className="w-full rounded-2xl border-2 border-dashed border-warmgray bg-[#FDFBF9] px-4 py-4 text-sm text-navy file:mr-4 file:rounded-full file:border-0 file:bg-navy file:px-4 file:py-2 file:text-xs file:font-semibold file:text-white"
                        />
                    )}
                    {memType === "video" && thumbnail && (
                        <p data-testid="thumbnail-ready" className="text-center font-mono text-[11px] uppercase tracking-widest text-navy">
                            Preview frame captured
                        </p>
                    )}

                    <input
                        data-testid="memory-title-input"
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Title (optional) — e.g. Grandpa's 80th birthday"
                        aria-label="Title"
                        className={INPUT_CLASS}
                    />
                    {memType !== "story" && (
                        <input
                            data-testid="memory-caption-input"
                            type="text"
                            value={caption}
                            onChange={(e) => setCaption(e.target.value)}
                            placeholder="Caption — who, when, what's the story?"
                            aria-label="Caption"
                            className={INPUT_CLASS}
                        />
                    )}
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div>
                            <label htmlFor="memory-date" className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-navy/60">
                                When did it happen?
                            </label>
                            <input
                                id="memory-date"
                                data-testid="memory-date-input"
                                type="date"
                                value={memoryDate}
                                onChange={(e) => setMemoryDate(e.target.value)}
                                className={INPUT_CLASS}
                            />
                        </div>
                        <div>
                            <label htmlFor="memory-location" className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-navy/60">
                                Where?
                            </label>
                            <input
                                id="memory-location"
                                data-testid="memory-location-input"
                                type="text"
                                value={location}
                                onChange={(e) => setLocation(e.target.value)}
                                placeholder="e.g. Bratislava"
                                className={INPUT_CLASS}
                            />
                        </div>
                    </div>

                    {milestones.length > 0 && (
                        <div>
                            <label htmlFor="memory-milestone" className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-navy/60">
                                Part of a milestone?
                            </label>
                            <select
                                id="memory-milestone"
                                data-testid="memory-milestone-select"
                                value={milestoneId}
                                onChange={(e) => setMilestoneId(e.target.value)}
                                className={INPUT_CLASS}
                            >
                                <option value="">No milestone</option>
                                {milestones.map((m) => (
                                    <option key={m.milestone_id} value={m.milestone_id}>
                                        {m.title}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {members.length > 0 && (
                        <div>
                            <p className="mb-1.5 font-mono text-[10px] uppercase tracking-widest text-navy/60">Who's in this memory?</p>
                            <div className="flex flex-wrap gap-2">
                                {members.map((m) => (
                                    <button
                                        key={m.user_id}
                                        type="button"
                                        data-testid={`tag-member-${m.user_id}`}
                                        onClick={() => toggleTag(m.user_id)}
                                        className={`flex items-center gap-1.5 rounded-full border-2 py-1 pl-1 pr-3 text-xs font-semibold transition-colors duration-300 ${
                                            tagged.includes(m.user_id)
                                                ? "border-navy bg-navy text-white"
                                                : "border-warmgray text-navy hover:border-peach"
                                        }`}
                                    >
                                        <Avatar person={m} size={20} />
                                        {m.name || m.email}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {uploading && progress > 0 && (
                        <div data-testid="upload-progress" className="space-y-1.5">
                            <div className="h-2 w-full overflow-hidden rounded-full bg-warmgray">
                                <div
                                    className="h-full rounded-full bg-gradient-to-r from-lavender to-peach transition-[width] duration-300"
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                            <p className="text-center font-mono text-[10px] uppercase tracking-widest text-navy/60">
                                Uploading large file · {progress}%
                            </p>
                        </div>
                    )}
                    <button
                        data-testid="memory-upload-button"
                        type="submit"
                        disabled={uploading || (memType !== "story" && !file)}
                        className="btn-gradient flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                    >
                        {uploading && <Loader2 size={15} className="animate-spin" />}
                        {uploading ? "Uploading…" : "Save to Family Archive"}
                    </button>
                </form>
            </motion.div>
        </div>
    );
}
